// Quick-math for an amount field: exact rational arithmetic over bigint, never eval or a float.
import { cleanAmountInput, type AmountFormat } from './amount-input.ts'

interface Fraction {
  readonly n: bigint
  readonly d: bigint
}

const OPERATORS: Readonly<Record<string, string>> = {
  '+': '+',
  '-': '-',
  '−': '-',
  '*': '*',
  '×': '*',
  x: '*',
  X: '*',
  '/': '/',
  '÷': '/',
  '(': '(',
  ')': ')',
}

const isDigit = (ch: string): boolean => ch >= '0' && ch <= '9'
const isBlank = (ch: string): boolean => ch === ' ' || ch === ' '

/** True when `raw` holds an operator or parenthesis beyond one leading minus sign. */
export function isAmountExpression(raw: string): boolean {
  const text = raw.trim()
  return [...text].some((ch, index) => {
    const op = OPERATORS[ch]
    return op !== undefined && !(op === '-' && index === 0)
  })
}

type Token =
  { readonly kind: 'op'; readonly op: string } | { readonly kind: 'number'; readonly value: Fraction }

function parseOperand(text: string, format: AmountFormat): Fraction | undefined {
  let separators = 0
  let digits = 0
  for (const ch of text) {
    if (isDigit(ch)) digits++
    else if (ch === format.groupSeparator || isBlank(ch)) continue
    else if (ch === '.' || ch === ',') separators++
    else return undefined
  }
  if (digits === 0 || separators > 1) return undefined
  const clean = cleanAmountInput(text, { ...format, decimalDigits: text.length, allowNegative: false })
  const dot = clean.indexOf('.')
  if (dot === -1) return { n: BigInt(clean), d: 1n }
  const fraction = clean.slice(dot + 1)
  return { n: BigInt(`${clean.slice(0, dot) || '0'}${fraction}`), d: 10n ** BigInt(fraction.length) }
}

function tokenize(raw: string, format: AmountFormat): Token[] | undefined {
  const tokens: Token[] = []
  let operand = ''
  const flush = (): boolean => {
    if (operand.trim() === '') {
      operand = ''
      return true
    }
    const value = parseOperand(operand, format)
    operand = ''
    if (value === undefined) return false
    tokens.push({ kind: 'number', value })
    return true
  }
  for (const ch of raw) {
    const op = OPERATORS[ch]
    if (op === undefined) {
      operand += ch
      continue
    }
    if (!flush()) return undefined
    tokens.push({ kind: 'op', op })
  }
  return flush() ? tokens : undefined
}

const add = (a: Fraction, b: Fraction): Fraction => ({ n: a.n * b.d + b.n * a.d, d: a.d * b.d })
const negate = (a: Fraction): Fraction => ({ n: -a.n, d: a.d })
const multiply = (a: Fraction, b: Fraction): Fraction => ({ n: a.n * b.n, d: a.d * b.d })
const divide = (a: Fraction, b: Fraction): Fraction | undefined =>
  b.n === 0n ? undefined : { n: a.n * b.d * (b.n < 0n ? -1n : 1n), d: a.d * (b.n < 0n ? -b.n : b.n) }

// expr := term (('+' | '-') term)*; term := factor (('*' | '/') factor)*;
// factor := ('+' | '-') factor | '(' expr ')' | number
function evaluate(tokens: readonly Token[]): Fraction | undefined {
  let at = 0
  const peekOp = (): string | undefined => {
    const token = tokens[at]
    return token?.kind === 'op' ? token.op : undefined
  }

  function factor(): Fraction | undefined {
    const token = tokens[at]
    if (token === undefined) return undefined
    if (token.kind === 'number') {
      at++
      return token.value
    }
    if (token.op === '-' || token.op === '+') {
      at++
      const inner = factor()
      return inner === undefined || token.op === '+' ? inner : negate(inner)
    }
    if (token.op !== '(') return undefined
    at++
    const inner = expr()
    if (inner === undefined || peekOp() !== ')') return undefined
    at++
    return inner
  }

  function term(): Fraction | undefined {
    let left = factor()
    while (left !== undefined && (peekOp() === '*' || peekOp() === '/')) {
      const op = peekOp()
      at++
      const right = factor()
      if (right === undefined) return undefined
      left = op === '*' ? multiply(left, right) : divide(left, right)
    }
    return left
  }

  function expr(): Fraction | undefined {
    let left = term()
    while (left !== undefined && (peekOp() === '+' || peekOp() === '-')) {
      const op = peekOp()
      at++
      const right = term()
      if (right === undefined) return undefined
      left = add(left, op === '-' ? negate(right) : right)
    }
    return left
  }

  const result = expr()
  return at === tokens.length ? result : undefined
}

/** Half away from zero, matching how an edit field opens an existing amount. */
function toCanonical(value: Fraction, decimalDigits: number): string {
  const scale = 10n ** BigInt(decimalDigits)
  const magnitude = value.n < 0n ? -value.n : value.n
  const units = (magnitude * scale * 2n + value.d) / (2n * value.d)
  const digits = units.toString().padStart(decimalDigits + 1, '0')
  const integer = digits.slice(0, digits.length - decimalDigits)
  const fraction = digits.slice(digits.length - decimalDigits).replace(/0+$/, '')
  const body = fraction === '' ? integer : `${integer}.${fraction}`
  return value.n < 0n && units !== 0n ? `-${body}` : body
}

/**
 * The canonical value of an arithmetic expression typed in `format`'s locale, rounded to its
 * decimal digits; `undefined` when incomplete, malformed, dividing by zero or a disallowed negative.
 */
export function evaluateAmountExpression(raw: string, format: AmountFormat): string | undefined {
  const tokens = tokenize(raw, format)
  const value = tokens === undefined ? undefined : evaluate(tokens)
  if (value === undefined) return undefined
  const canonical = toCanonical(value, format.decimalDigits)
  return canonical.startsWith('-') && format.allowNegative !== true ? undefined : canonical
}
